// @ts-nocheck
import React from "react";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";

// The stacked charts, each in a band (its own toolbar in a column down its right edge) that can be dragged into a new order by a small grip on its left edge. The handle is never the chart itself, so the chart keeps its own hover, pan and zoom.
const ChartBand = ({ id, label, controls, children }) => {
  const { setNodeRef, listeners, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: transform ? `translate3d(0, ${Math.round(transform.y)}px, 0)` : undefined,
        transition,
        zIndex: isDragging ? 1 : undefined,
      }}
      className={`relative flex min-h-[14rem] shrink-0 flex-1 flex-row border-t border-eai-border bg-eai-surface-card first:border-t-0 ${
        isDragging ? "opacity-90 shadow-lg" : ""
      }`}
    >
      {/* White padding around a rounded grip, so it reads as a handle set into the card rather than a bar glued to its edge. touch-action none, or on a phone the page scrolls instead of the band moving. */}
      <div className="flex shrink-0 bg-eai-surface-card py-2">
        <div
          {...listeners}
          role="button"
          title="Drag to reorder"
          aria-label={`Reorder ${label}`}
          className="flex w-6 cursor-grab select-none items-center justify-center rounded-lg bg-eai-surface text-eai-ink-subtle transition-colors hover:bg-eai-border active:cursor-grabbing active:bg-eai-border"
          style={{ touchAction: "none" }}
        >
          <span aria-hidden="true" className="text-base leading-none tracking-[-0.15em]">⋮⋮</span>
        </div>
      </div>
      <div className="flex min-h-0 min-w-0 flex-1 flex-row pl-2">
        <div className="min-h-0 min-w-0 flex-1">{children}</div>
        {controls}
      </div>
    </div>
  );
};

const SortableChartStack = ({ items, onReorder }) => {
  // The grip is an explicit handle, so a few pixels of movement starts the drag
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const keys = items.map((item) => item.key);

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = keys.indexOf(active.id);
    const to = keys.indexOf(over.id);
    if (from < 0 || to < 0) return;
    onReorder(arrayMove(keys, from, to));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={keys} strategy={verticalListSortingStrategy}>
        <div className="flex h-full min-h-0 flex-col overflow-y-auto">
          {items.map((item) => (
            <ChartBand key={item.key} id={item.key} label={item.label} controls={item.controls}>
              {item.node}
            </ChartBand>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default SortableChartStack;
